import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, Share } from "lucide-react"
import { ShareReportFormValues } from "../../types/share-report"
import { ShareReportModal } from "./share-report-modal"
import { useShareReportMutation } from "@app.raytd.com/store"
import { toast } from "sonner"
import { useLocation, useNavigate } from "react-router-dom"
import { ReportPermissions } from "store/src/lib/services/types"
import { SaveReportModal } from "./save-report-modal"

interface ShareButtonProps {
  revisionId: string
  variant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive"
  size?: "default" | "sm" | "lg" | "icon"
  className?: string
  isAllowShareConfigurable?: boolean
  defaultAllowShare?: boolean
  isAllowExportConfigurable?: boolean
  defaultAllowExport?: boolean
  isAnonymous?: boolean
  permissions?: ReportPermissions
}

export function ShareButton({
  revisionId,
  variant = "default",
  size = "default",
  className,
  isAllowShareConfigurable,
  defaultAllowShare,
  isAllowExportConfigurable,
  defaultAllowExport,
  isAnonymous,
  permissions,
}: ShareButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const [shareReport, { isLoading: isSharing }] = useShareReportMutation();

  const navigate = useNavigate();
  const location = useLocation();

  const handleContinue = useCallback(() => {
    if (!permissions) return

    if (permissions.guestHasAccount) {
      navigate('/login', { state: { from: location } });
    } else {
      navigate('/signup', {
        state: {
          from: location,
          ...(permissions.user && {
            invite: {
              token: 'redirect',
              user: {
                email: permissions.user.email,
                first_name: permissions.user.first_name,
                last_name: permissions.user.last_name,
              }
            }
          })
        }
      });
    }
  }, [permissions, navigate, location]);

  const handleShare = useCallback(async (data: ShareReportFormValues) => {
    try {
      await shareReport({ revisionId, data }).unwrap();
      toast.success("Report shared successfully");
      setIsModalOpen(false)
    } catch (error) {
      toast.error("Failed to share report");
    }

  }, [revisionId, shareReport]);

  if (isAnonymous) {
    let message = `You'll need to create a free account to share this report.`
    if (permissions?.guestHasAccount) {
      message = `We've noticed you've already created an account. Please login to share this report.`
    }

    return (
      <SaveReportModal
        isOpen={isAuthModalOpen}
        onOpenChange={setIsAuthModalOpen}
        onContinue={handleContinue}
        message={message}
        title="Share Report"
        trigger={<Button variant={variant} size={size} className={className}
          disabled={isSharing}
        >
          {isSharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share className="mr-2 h-4 w-4" />}
          Share
        </Button>}
      />
    );
  }

  return (
    <>
      <Button variant={variant} size={size} className={className}
        onClick={() => setIsModalOpen(true)}
        disabled={isSharing}
      >
        {isSharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share className="mr-2 h-4 w-4" />}
        Share
      </Button>

      <ShareReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleShare}
        isAllowShareConfigurable={isAllowShareConfigurable}
        defaultAllowShare={defaultAllowShare}
        isAllowExportConfigurable={isAllowExportConfigurable}
        defaultAllowExport={defaultAllowExport}
      />
    </>
  )
}

